// LaTeX parser — converts LaTeX source to TipTap JSON document
// Handles: document metadata, sections, theorem environments, math, lists
// Strips out preamble (\documentclass, \usepackage, etc.) if present

// Helper: extract content within balanced braces
function extractBalancedBraces(str: string, startIdx: number): { content: string; endIdx: number } | null {
    if (str[startIdx] !== "{") return null;
    let depth = 1;
    let i = startIdx + 1;
    while (i < str.length && depth > 0) {
        if (str[i] === "{" && str[i - 1] !== "\\") depth++;
        else if (str[i] === "}" && str[i - 1] !== "\\") depth--;
        i++;
    }
    if (depth !== 0) return null;
    return { content: str.slice(startIdx + 1, i - 1), endIdx: i };
}

// Helper: clean up inline LaTeX text by stripping common wrappers
function cleanInlineLatex(text: string): string {
    let result = text;

    // Strip \mbox{...} → content (handles nested braces)
    result = result.replace(/\\mbox\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Strip \text{...} → content (used inside math mode)
    result = result.replace(/\\text\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Strip \textrm{...} → content
    result = result.replace(/\\textrm\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Strip \textit{...} → content (italic)
    result = result.replace(/\\textit\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Strip \textbf{...} → content (bold)
    result = result.replace(/\\textbf\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Strip \texttt{...} → content (monospace)
    result = result.replace(/\\texttt\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Strip \emph{...} → content (emphasis)
    result = result.replace(/\\emph\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Strip \label{...} → empty (labels are for cross-referencing)
    result = result.replace(/\\label\s*\{[^}]*\}/g, "");

    // Transform \ref{...} → [ref] placeholder
    result = result.replace(/\\ref\s*\{([^}]*)\}/g, "[ref:$1]");

    // Transform \eqref{...} → (ref) placeholder
    result = result.replace(/\\eqref\s*\{([^}]*)\}/g, "(ref:$1)");

    // Transform \cite{...} → [cite] placeholder
    result = result.replace(/\\cite\s*\{([^}]*)\}/g, "[$1]");

    // Strip \footnote{...} → empty (or could show as superscript)
    result = result.replace(/\\footnote\s*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g, "");

    // Strip formatting commands that we can't visually represent yet
    // \textsf, \textsc, \textsl
    result = result.replace(/\\text(?:sf|sc|sl)\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");

    // Handle old-style {\it ...} and {\bf ...} formatting
    // {\it Proof.} → Proof.
    result = result.replace(/\{\\it\s+([^}]*)\}/g, "$1");
    result = result.replace(/\{\\bf\s+([^}]*)\}/g, "$1");
    result = result.replace(/\{\\rm\s+([^}]*)\}/g, "$1");
    result = result.replace(/\{\\em\s+([^}]*)\}/g, "$1");
    result = result.replace(/\{\\sl\s+([^}]*)\}/g, "$1");
    result = result.replace(/\{\\sc\s+([^}]*)\}/g, "$1");

    // Clean up multiple spaces
    result = result.replace(/\s+/g, " ").trim();

    return result;
}

// Helper: expand \newcommand macros in LaTeX source
function expandMacros(latex: string): string {
    // Parse all \newcommand{\name}{expansion} and \newcommand{\name}[n]{expansion}
    const macros: Map<string, { argCount: number; body: string }> = new Map();

    // Match \newcommand{\name}[optional argcount]{body}
    // Also match \renewcommand and \def
    const cmdRegex = /\\(?:re)?newcommand\{\\([a-zA-Z]+)\}(?:\[(\d+)\])?\s*\{/g;
    let match;
    while ((match = cmdRegex.exec(latex)) !== null) {
        const name = match[1];
        const argCount = match[2] ? parseInt(match[2]) : 0;
        const braceStart = match.index + match[0].length - 1;
        const extracted = extractBalancedBraces(latex, braceStart);
        if (extracted) {
            macros.set(name, { argCount, body: extracted.content });
        }
    }

    // Handle \def\name{body} (simpler form, no args)
    // Also handle \def\name#1#2{body} with args
    const defRegex = /\\def\\([a-zA-Z]+)((?:#\d)*)\s*\{/g;
    while ((match = defRegex.exec(latex)) !== null) {
        const name = match[1];
        const argSpec = match[2] || "";
        const argCount = (argSpec.match(/#/g) || []).length;
        const braceStart = match.index + match[0].length - 1;
        const extracted = extractBalancedBraces(latex, braceStart);
        if (extracted && !macros.has(name)) {
            macros.set(name, { argCount, body: extracted.content });
        }
    }

    if (macros.size === 0) return latex;

    // Now expand macros in the document body
    let result = latex;

    // Multiple expansion passes to handle nested macros
    for (let pass = 0; pass < 3; pass++) {
        // Expand macros without arguments first (simpler)
        for (const [name, { argCount, body }] of macros) {
            if (argCount === 0) {
                // Simple replacement: \name -> body
                // Be careful not to replace \name in \newcommand{\name}
                const pattern = new RegExp(`\\\\${name}(?![a-zA-Z])`, "g");
                result = result.replace(pattern, body);
            }
        }
    }

    // Expand macros with arguments
    for (const [name, { argCount, body }] of macros) {
        if (argCount > 0) {
            // Find \name{arg1}{arg2}... and replace with body substituting #1, #2, etc.
            const pattern = new RegExp(`\\\\${name}(?![a-zA-Z])`, "g");
            let newResult = "";
            let lastIdx = 0;
            let m;
            while ((m = pattern.exec(result)) !== null) {
                newResult += result.slice(lastIdx, m.index);
                let idx = m.index + m[0].length;

                // Skip optional whitespace
                while (idx < result.length && /\s/.test(result[idx])) idx++;

                // Extract arguments
                const args: string[] = [];
                for (let a = 0; a < argCount && idx < result.length; a++) {
                    if (result[idx] === "{") {
                        const extracted = extractBalancedBraces(result, idx);
                        if (extracted) {
                            args.push(extracted.content);
                            idx = extracted.endIdx;
                        } else {
                            break;
                        }
                    } else {
                        // Single character argument
                        args.push(result[idx]);
                        idx++;
                    }
                }

                // Substitute arguments into body
                let expanded = body;
                for (let a = 0; a < args.length; a++) {
                    expanded = expanded.replace(new RegExp(`#${a + 1}`, "g"), args[a]);
                }
                newResult += expanded;
                lastIdx = idx;
            }
            newResult += result.slice(lastIdx);
            result = newResult;
        }
    }

    return result;
}

export function parseLatexToDoc(latex: string): any {
    if (!latex || typeof latex !== 'string') {
        console.error(`[parseLatexToDoc] invalid input: ${typeof latex}`);
    }

    // Protect TikZ environments from macro expansion — they contain raw TeX
    // that must be passed verbatim to TikZJax / the LaTeX compiler.
    const tikzPlaceholders: Map<string, string> = new Map();
    let tikzIdx = 0;
    latex = latex.replace(
        /\\begin\{(tikzpicture|tikzcd)\}[\s\S]*?\\end\{\1\}/g,
        (match) => {
            const key = `%%TIKZ_PLACEHOLDER_${tikzIdx++}%%`;
            tikzPlaceholders.set(key, match);
            return key;
        },
    );

    // Collect \def commands BEFORE expandMacros mangles them
    // (expandMacros replaces \s inside \def\s{2.0} turning it into \def2.0{2.0})
    const defPatternPre = /\\def\\[a-zA-Z]+(?:#\d)*\s*\{[^{}]*(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}[^{}]*)*\}/g;
    const newcmdPatternPre = /\\(?:re)?newcommand\{?\\[a-zA-Z]+\}?(?:\[\d+\])?\s*\{[^{}]*(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}[^{}]*)*\}/g;
    const collectedDefs: string[] = [];
    let defMatch;
    while ((defMatch = defPatternPre.exec(latex)) !== null) {
        collectedDefs.push(defMatch[0]);
    }
    while ((defMatch = newcmdPatternPre.exec(latex)) !== null) {
        collectedDefs.push(defMatch[0]);
    }

    // First, expand all macros
    latex = expandMacros(latex);

    // NOTE: TikZ placeholders are NOT restored yet — they must survive all the
    // preprocessing / stripping passes below so that \def commands (and other
    // raw TeX) inside tikzpicture environments are not mangled or removed.

    // Convert LaTeX accents to Unicode (common ones)
    const accentMap: [RegExp, string | ((_: string, c: string) => string)][] = [
        [/\\'([aeiouAEIOU])/g, (_: string, c: string) => ({ a: "á", e: "é", i: "í", o: "ó", u: "ú", A: "Á", E: "É", I: "Í", O: "Ó", U: "Ú" } as Record<string, string>)[c] || c],
        [/\\\`([aeiouAEIOU])/g, (_: string, c: string) => ({ a: "à", e: "è", i: "ì", o: "ò", u: "ù", A: "À", E: "È", I: "Ì", O: "Ò", U: "Ù" } as Record<string, string>)[c] || c],
        [/\\\"([aeiouAEIOU])/g, (_, c) => ({ a: "ä", e: "ë", i: "ï", o: "ö", u: "ü", A: "Ä", E: "Ë", I: "Ï", O: "Ö", U: "Ü" } as any)[c] || c],
        [/\\\^([aeiouAEIOU])/g, (_, c) => ({ a: "â", e: "ê", i: "î", o: "ô", u: "û", A: "Â", E: "Ê", I: "Î", O: "Ô", U: "Û" } as any)[c] || c],
        [/\\~([nNaAoO])/g, (_, c) => ({ n: "ñ", N: "Ñ", a: "ã", A: "Ã", o: "õ", O: "Õ" } as any)[c] || c],
        [/\\c\{([cC])\}/g, (_, c) => c === "c" ? "ç" : "Ç"],
        [/\\v\{([sczSCZ])\}/g, (_, c) => ({ s: "š", c: "č", z: "ž", S: "Š", C: "Č", Z: "Ž" } as any)[c] || c],
        [/\\'c/g, "ć"], [/\\'C/g, "Ć"],  // Special case for ć
        [/\\v\s*c/g, "č"], [/\\v\s*s/g, "š"], [/\\v\s*z/g, "ž"],
    ];
    for (const [pattern, replacement] of accentMap) {
        latex = latex.replace(pattern, replacement as any);
    }

    // Preprocess AMSTeX-specific constructs:
    // 1. Strip \leqno(...) from display math (equation labels)
    latex = latex.replace(/\\leqno\s*\([^)]*\)/g, "");

    // 2. Convert {\it ...} to \textit{...} and {\bf ...} to \textbf{...}
    //    This is a simplified conversion for common cases
    latex = latex.replace(/\{\\it\s+([^{}]+(?:\{[^{}]*\}[^{}]*)*)\}/g, "\\textit{$1}");
    latex = latex.replace(/\{\\bf\s+([^{}]+(?:\{[^{}]*\}[^{}]*)*)\}/g, "\\textbf{$1}");
    latex = latex.replace(/\{\\rm\s+([^{}]+(?:\{[^{}]*\}[^{}]*)*)\}/g, "$1");
    latex = latex.replace(/\{\\tt\s+([^{}]+(?:\{[^{}]*\}[^{}]*)*)\}/g, "\\texttt{$1}");

    // 3. Handle AMSTeX \roman command in math: {\roman d} → \mathrm{d}, {\roman e} → \mathrm{e}
    latex = latex.replace(/\{\\roman\s+([a-zA-Z]+)\}/g, "\\mathrm{$1}");
    latex = latex.replace(/\\roman\s+([a-zA-Z])/g, "\\mathrm{$1}");
    // Also handle plain {\roman{...}}
    latex = latex.replace(/\\roman\{([^}]+)\}/g, "\\mathrm{$1}");

    // 4. Convert AMSTeX fractions to standard LaTeX:
    //    {\textstyle{a\over b}} → \tfrac{a}{b}
    //    {a\over b} → \frac{a}{b}
    latex = latex.replace(/\{\\textstyle\{([^{}\\]+)\\over\s*([^{}]+)\}\}/g, "\\tfrac{$1}{$2}");
    latex = latex.replace(/\{([^{}\\]+)\\over\s*([^{}]+)\}/g, "\\frac{$1}{$2}");
    // Handle \txt{a\over b} which also appears
    latex = latex.replace(/\\txt\{([^{}\\]+)\\over\s*([^{}]+)\}/g, "\\tfrac{$1}{$2}");

    // 5. Convert AMSTeX font commands to standard:
    //    \Cal → \mathcal, \Bbb → \mathbb
    latex = latex.replace(/\\Cal\s+([A-Z])/g, "\\mathcal{$1}");
    latex = latex.replace(/\\Cal\{([^}]+)\}/g, "\\mathcal{$1}");
    latex = latex.replace(/\\Bbb\s+([A-Z])/g, "\\mathbb{$1}");
    latex = latex.replace(/\\Bbb\{([^}]+)\}/g, "\\mathbb{$1}");

    // 6. Strip AMSTeX formatting commands
    latex = latex.replace(/\\(medskip|bigskip|smallskip|medbreak|bigbreak|smallbreak|goodbreak|allowbreak)/g, "");

    // 7. Strip AMSTeX page formatting commands (\headline{...}, \footline{...})
    //    These can span multiple lines and contain complex nested braces
    latex = latex.replace(/\\headline\s*\{[^{}]*(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}[^{}]*)*\}/g, "");
    latex = latex.replace(/\\footline\s*\{[^{}]*(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}[^{}]*)*\}/g, "");

    // 8. Strip \def commands (already collected before expandMacros for TikZ)
    //    Note: expandMacros may mangle \def\s{2.0} → \def2.0{2.0}, so also strip
    //    the mangled form (\def followed by non-backslash content then braces)
    latex = latex.replace(/\\def\\[a-zA-Z]+(?:#\d)*\s*\{[^{}]*(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}[^{}]*)*\}/g, "");
    latex = latex.replace(/\\def[^\\{}\n]+\{[^{}]*\}/g, "");

    // 9. Strip \font commands (font definitions)
    latex = latex.replace(/\\font\\[a-zA-Z]+=\S+(?:\s+at\s+\S+)?/g, "");

    // 10. Strip \newfam, \textfont, \scriptfont commands
    latex = latex.replace(/\\(newfam|textfont|scriptfont|scriptscriptfont)\\?[a-zA-Z]*=[^\\]*(?=\\|$|\n)/g, "");

    // 11. Strip AMSTeX preamble commands that appear in body
    latex = latex.replace(/\\input\s+\S+\.tex/g, "");
    latex = latex.replace(/\\documentstyle\{[^}]*\}/g, "");
    latex = latex.replace(/\\magnification\d+/g, "");
    latex = latex.replace(/\\[hv]size\s*=\s*\S+/g, "");
    latex = latex.replace(/\\[hv]offset\s*=\s*\S+/g, "");
    latex = latex.replace(/\\nopagenumbers/g, "");
    latex = latex.replace(/\\baselineskip\s*=\s*\S+/g, "");

    // 12. Strip paragraph wrapper environments used in papers like Perelman's
    //     \begin{parag}, \end{parag}, \begin{intropar}, \end{intropar}, etc.
    //     These are just structural wrappers, not semantic environments
    latex = latex.replace(/\\begin\{(parag|intropar|parstr)\}\s*/g, "");
    latex = latex.replace(/\\end\{(parag|intropar|parstr)\}\s*/g, "");
    // Also strip standalone \par commands
    latex = latex.replace(/\\par(?=\s|$)/g, "");

    // Extract metadata from preamble BEFORE stripping to body
    // (In amsart, \title and \author appear before \begin{document})
    // Also handles AMSTeX \topmatter style
    const preambleMetadata: { title?: string; authors: string[]; abstract?: string } = { authors: [] };

    // Normalize % line continuations: \title[...]%\n{...} → \title[...]{...}
    const normalizedLatex = latex.replace(/%[ \t]*\n[ \t]*/g, "");

    // Extract \title[optional]{content} - handle nested braces like \thanks{}
    // Also handles AMSTeX \title ... \endtitle format (without braces)
    const titleCmdMatch = normalizedLatex.match(/\\title(?:\[[^\]]*\])?\s*\{/);
    if (titleCmdMatch) {
        const braceStart = titleCmdMatch.index! + titleCmdMatch[0].length - 1;
        const extracted = extractBalancedBraces(normalizedLatex, braceStart);
        if (extracted) {
            let title = extracted.content;
            // Strip \thanks{...} - handle nested braces
            title = title.replace(/\\thanks\s*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g, "");
            // Convert \\ (line break) to space
            title = title.replace(/\\\\/g, " ");
            // Clean up whitespace
            title = title.replace(/\s+/g, " ").trim();
            preambleMetadata.title = title;
        }
    }

    // AMSTeX-style: \title ... \endtitle (without braces)
    if (!preambleMetadata.title) {
        const amstexTitleMatch = normalizedLatex.match(/\\title\s*\n([\s\S]*?)\\endtitle/);
        if (amstexTitleMatch) {
            preambleMetadata.title = amstexTitleMatch[1].replace(/\\\\/g, " ").replace(/\s+/g, " ").trim();
        }
    }

    // Extract all \author[optional]{name} commands - handle nested braces
    const authorCmdRegex = /\\author(?:\[[^\]]*\])?\s*\{/g;
    let authorCmdMatch;
    while ((authorCmdMatch = authorCmdRegex.exec(normalizedLatex)) !== null) {
        const braceStart = authorCmdMatch.index + authorCmdMatch[0].length - 1;
        const extracted = extractBalancedBraces(normalizedLatex, braceStart);
        if (extracted) {
            let authorName = extracted.content;
            // Strip \thanks{...} - handle nested braces (e.g., \thanks{affiliation with {nested} text})
            authorName = authorName.replace(/\\thanks\s*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g, "");
            // Clean up whitespace
            authorName = authorName.replace(/\s+/g, " ").trim();
            preambleMetadata.authors.push(authorName);
        }
    }

    // AMSTeX-style: \author ... \endauthor (without braces)
    if (preambleMetadata.authors.length === 0) {
        const amstexAuthorMatch = normalizedLatex.match(/\\author\s+([\s\S]*?)\\endauthor/);
        if (amstexAuthorMatch) {
            preambleMetadata.authors.push(amstexAuthorMatch[1].trim());
        }
    }

    // AMSTeX-style: \abstract{...}\endabstract
    const amstexAbstractMatch = normalizedLatex.match(/\\abstract\s*\{([\s\S]*?)\}\s*\\endabstract/);
    if (amstexAbstractMatch) {
        preambleMetadata.abstract = amstexAbstractMatch[1].trim();
    }

    // Helper: strip all occurrences of \cmdName{...} (with balanced braces) from text
    function stripBodyCmd(text: string, cmdName: string): string {
        const cmdRe = new RegExp(`\\\\${cmdName}(?:\\[[^\\]]*\\])?\\s*\\{`);
        let out = "";
        let pos = 0;
        while (pos < text.length) {
            const sub = text.slice(pos);
            const m = cmdRe.exec(sub);
            if (!m) { out += sub; break; }
            const matchStart = pos + m.index;
            const braceStart = matchStart + m[0].length - 1;
            out += text.slice(pos, matchStart);
            const extracted = extractBalancedBraces(text, braceStart);
            pos = extracted ? extracted.endIdx : matchStart + 1;
        }
        return out;
    }

    // Detect two-column layout from \documentclass before stripping the preamble.
    // Classes/options that imply a two-column document:
    //   \documentclass{aa}              ← A&A (always two-column)
    //   \documentclass{IEEEtran}        ← IEEE (always two-column)
    //   \documentclass[twocolumn]{...}  ← explicit option
    //   \documentclass[aps,...]{revtex4-2} with twocolumn or reprint option
    const docclassMatch = latex.match(/\\documentclass\s*(?:\[([^\]]*)\])?\s*\{([^}]+)\}/);
    const isTwoColumnDoc = (() => {
        if (!docclassMatch) return false;
        const opts = (docclassMatch[1] ?? "").toLowerCase();
        const cls = docclassMatch[2].trim().toLowerCase();
        // Classes that are inherently two-column
        if (cls === "aa" || cls === "ieetran" || cls === "ieeetran") return true;
        // Explicit option
        if (opts.includes("twocolumn") || opts.includes("reprint")) return true;
        return false;
    })();

    // Now extract just the document body
    let bodyLatex = latex;
    const beginDocMatch = latex.match(/\\begin\{document\}([\s\S]*)\\end\{document\}/);
    if (beginDocMatch) {
        bodyLatex = beginDocMatch[1].trim();
    } else {
        // For AMSTeX: strip everything before \endtopmatter or first \head
        const endTopmatterMatch = latex.match(/\\endtopmatter\s*([\s\S]*)/);
        if (endTopmatterMatch) {
            bodyLatex = endTopmatterMatch[1].trim();
        } else {
            // Try to find first \head as body start
            const firstHeadMatch = latex.match(/(\\head[\s\S]*)/);
            if (firstHeadMatch) {
                bodyLatex = firstHeadMatch[1].trim();
            }
        }
    }

    // Pre-process body: strip \title{...}, \author{...}, \maketitle that appear
    // inside \begin{document} in amsart papers (already extracted from preamble)
    bodyLatex = stripBodyCmd(bodyLatex, "title");
    bodyLatex = stripBodyCmd(bodyLatex, "author");
    bodyLatex = bodyLatex.replace(/\\maketitle\b/g, "");

    // Pre-process body: convert $$...$$ display math to \[...\] for uniform handling.
    // Non-greedy match handles consecutive blocks correctly.
    bodyLatex = bodyLatex.replace(/\$\$([\s\S]+?)\$\$/g, (_match, inner) => {
        const trimmed = inner.trim();
        if (!trimmed) return "";
        return `\\[\n${trimmed}\n\\]`;
    });

    // Split into lines, stripping end-of-line comments (but not % inside math)
    // First, restore TikZ environments from placeholders so the line-by-line
    // parser can capture them intact (with their original \def commands etc.)
    for (const [key, original] of tikzPlaceholders) {
        bodyLatex = bodyLatex.replace(key, original);
    }
    const rawLines = bodyLatex.split("\n");
    const lines: string[] = rawLines.map(line => {
        // Don't strip if line is entirely a comment (starts with %)
        if (line.trim().startsWith("%")) return "";
        // Strip trailing comments (% not preceded by \)
        return line.replace(/(?<!\\)%.*$/, "");
    });
    const content: any[] = [];
    let i = 0;

    // Add preamble metadata to content first
    if (preambleMetadata.title) {
        content.push({
            type: "docTitle",
            content: parseInlineStatic(preambleMetadata.title),
        });
    }
    if (preambleMetadata.authors.length > 0) {
        content.push({
            type: "docAuthor",
            content: parseInlineStatic(preambleMetadata.authors.join(" and ")),
        });
    }
    // Add abstract from AMSTeX preamble if found
    if (preambleMetadata.abstract) {
        content.push({
            type: "docAbstract",
            content: [{ type: "paragraph", content: parseInlineStatic(preambleMetadata.abstract) }],
        });
    }

    // Static version of parseInline for use before the main loop
    function parseInlineStatic(text: string): any[] {
        // Clean up inline LaTeX wrappers first
        text = cleanInlineLatex(text);

        const nodes: any[] = [];
        const inlineMathRegex = /\$([^$]+)\$/g;
        let lastIndex = 0;
        let match;

        while ((match = inlineMathRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                const before = text.slice(lastIndex, match.index);
                if (before) nodes.push({ type: "text", text: before });
            }
            nodes.push({ type: "inlineMath", attrs: { latex: match[1] } });
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
            const remaining = text.slice(lastIndex);
            if (remaining) nodes.push({ type: "text", text: remaining });
        }

        // Filter out empty text nodes — ProseMirror schema.nodeFromJSON throws on { type: "text", text: "" }
        return nodes.filter(n => !(n.type === "text" && !n.text));
    }

    // Helper: consume whitespace/empty lines
    function skipEmpty() {
        while (i < lines.length && lines[i].trim() === "") i++;
    }

    // Helper: parse inline content (text + math)
    function parseInline(text: string): any[] {
        // Clean up inline LaTeX wrappers first
        text = cleanInlineLatex(text);

        const nodes: any[] = [];
        const inlineMathRegex = /\$([^$]+)\$/g;
        let lastIndex = 0;
        let match;

        while ((match = inlineMathRegex.exec(text)) !== null) {
            // Text before math
            if (match.index > lastIndex) {
                const before = text.slice(lastIndex, match.index);
                if (before) nodes.push({ type: "text", text: before });
            }
            // Math node
            nodes.push({ type: "inlineMath", attrs: { latex: match[1] } });
            lastIndex = match.index + match[0].length;
        }

        // Remaining text
        if (lastIndex < text.length) {
            const remaining = text.slice(lastIndex);
            if (remaining) nodes.push({ type: "text", text: remaining });
        }

        // Filter out empty text nodes — ProseMirror schema.nodeFromJSON throws on { type: "text", text: "" }
        return nodes.filter(n => !(n.type === "text" && !n.text));
    }

    // Helper: parse a \command{...} with content
    function parseCommand(line: string, cmd: string): string | null {
        const regex = new RegExp(`\\\\${cmd}\\{([^}]*)\\}`);
        const match = line.match(regex);
        return match ? match[1] : null;
    }

    // Helper: parse begin/end environment
    function parseEnvironment(envName: string): any[] {
        const envContent: any[] = [];
        let depth = 1;
        i++; // skip \begin line

        const endPattern = `\\end{${envName}}`;
        const beginPattern = `\\begin{${envName}}`;

        while (i < lines.length && depth > 0) {
            const line = lines[i].trim();

            // Check for \end{envName} ANYWHERE in the line (not just start)
            const endIdx = line.indexOf(endPattern);
            if (endIdx !== -1) {
                depth--;
                if (depth === 0) {
                    // Text before \end{...} becomes content
                    const textBefore = line.slice(0, endIdx).trim();
                    if (textBefore) {
                        envContent.push({
                            type: "paragraph",
                            content: parseInline(textBefore),
                        });
                    }
                    // Text after \end{...} needs to be reprocessed in main loop
                    const textAfter = line.slice(endIdx + endPattern.length).trim();
                    if (textAfter) {
                        // Put the remaining text back for the main loop to process
                        lines[i] = textAfter;
                        // Don't increment i - let main loop process this line
                    } else {
                        i++;
                    }
                    break;
                }
            }

            // Check for nested \begin{envName}
            if (line.includes(beginPattern)) {
                depth++;
            }

            // Handle \begin{equation} and \begin{align} inside environments
            // Can appear mid-line after other content
            const mathEnvMatch = line.match(/\\begin\{(equation|align|gather|multline)(\*?)\}/);
            if (mathEnvMatch) {
                const mathEnv = mathEnvMatch[1];
                const starred = mathEnvMatch[2] === "*";
                const envPattern = new RegExp(`\\\\end\\s*\\{${mathEnv}\\*?\\}`);

                // Find where \begin{...} starts
                const beginIdx = line.indexOf(mathEnvMatch[0]);

                // Text before \begin{equation} becomes a paragraph
                const textBefore = line.slice(0, beginIdx).trim();
                if (textBefore) {
                    envContent.push({
                        type: "paragraph",
                        content: parseInline(textBefore),
                    });
                }

                // Content after \begin{equation} on same line
                let afterBegin = line.slice(beginIdx + mathEnvMatch[0].length).trim();
                let mathContent = "";

                // Check if \end{...} is on the same line
                if (envPattern.test(afterBegin)) {
                    const endMatch = afterBegin.match(envPattern);
                    if (endMatch) {
                        const endIdx = afterBegin.indexOf(endMatch[0]);
                        mathContent = afterBegin.slice(0, endIdx).trim();
                        const textAfter = afterBegin.slice(endIdx + endMatch[0].length).trim();
                        envContent.push({
                            type: mathEnv === "equation" && !starred ? "numberedEquation" : "displayMath",
                            attrs: { latex: mathContent },
                        });
                        // Process text after \end{equation} on same line
                        if (textAfter) {
                            lines[i] = textAfter;
                            continue;
                        }
                    }
                } else {
                    // Multi-line equation
                    mathContent = afterBegin;
                    i++;
                    while (i < lines.length && !envPattern.test(lines[i].trim())) {
                        mathContent += " " + lines[i].trim();
                        i++;
                    }
                    // Handle the \end line
                    if (i < lines.length) {
                        const endLine = lines[i].trim();
                        const endMatch = endLine.match(envPattern);
                        if (endMatch) {
                            const endIdx = endLine.indexOf(endMatch[0]);
                            mathContent += " " + endLine.slice(0, endIdx).trim();
                            const textAfter = endLine.slice(endIdx + endMatch[0].length).trim();
                            envContent.push({
                                type: mathEnv === "equation" && !starred ? "numberedEquation" : "displayMath",
                                attrs: { latex: mathContent.trim() },
                            });
                            if (textAfter) {
                                lines[i] = textAfter;
                                continue;
                            }
                        }
                    }
                }
                i++;
                continue;
            }

            // Handle $$...$$ display math within environments
            if (line.startsWith("$$")) {
                let mathContent = "";
                if (line.endsWith("$$") && line.length > 4) {
                    mathContent = line.slice(2, -2).trim();
                } else {
                    mathContent = line.slice(2).trim();
                    i++;
                    while (i < lines.length) {
                        const nextLine = lines[i].trim();
                        if (nextLine.endsWith("$$")) {
                            mathContent += " " + nextLine.slice(0, -2).trim();
                            i++;
                            break;
                        }
                        mathContent += " " + nextLine;
                        i++;
                    }
                }
                envContent.push({
                    type: "displayMath",
                    attrs: { latex: mathContent.trim() },
                });
                if (line.endsWith("$$") && line.length > 4) i++;
                continue;
            }

            // Handle display math \[ ... \] within environments
            if (line.includes("\\[")) {
                // Text before \[ becomes a paragraph
                const bracketIdx = line.indexOf("\\[");
                const textBefore = line.slice(0, bracketIdx).trim();
                if (textBefore) {
                    envContent.push({
                        type: "paragraph",
                        content: parseInline(textBefore),
                    });
                }

                let mathContent = "";
                const afterBracket = line.slice(bracketIdx + 2).trim();

                // Check if closing \] is on the same line
                if (afterBracket.includes("\\]")) {
                    mathContent = afterBracket.replace("\\]", "").trim();
                    envContent.push({
                        type: "displayMath",
                        attrs: { latex: mathContent.trim() },
                    });
                } else {
                    // Multi-line display math - collect until \]
                    mathContent = afterBracket;
                    i++;
                    while (i < lines.length) {
                        const nextLine = lines[i].trim();
                        if (nextLine.includes("\\]")) {
                            const closingIdx = nextLine.indexOf("\\]");
                            mathContent += " " + nextLine.slice(0, closingIdx).trim();
                            envContent.push({
                                type: "displayMath",
                                attrs: { latex: mathContent.trim() },
                            });
                            break;
                        }
                        mathContent += " " + nextLine;
                        i++;
                    }
                }
                i++;
                continue;
            }

            // Handle \begin{tikzpicture} / \begin{tikzcd} inside environments
            const tikzEnvMatch = line.match(/^\\begin\{(tikzpicture|tikzcd)\}/);
            if (tikzEnvMatch) {
                const tikzEnv = tikzEnvMatch[1];
                const tikzBodyLines: string[] = [line];
                let tikzDepth = 1;
                i++;
                while (i < lines.length && tikzDepth > 0) {
                    const tl = lines[i];
                    const ttrimmed = tl.trim();
                    if (ttrimmed.startsWith(`\\end{${tikzEnv}}`)) tikzDepth--;
                    else if (ttrimmed.match(new RegExp(`^\\\\begin\\{${tikzEnv}\\*?\\}`))) tikzDepth++;
                    tikzBodyLines.push(tl);
                    i++;
                }
                const defsPrefix = collectedDefs.length > 0 ? collectedDefs.join("\n") + "\n" : "";
                envContent.push({
                    type: "tikzBlock",
                    attrs: { source: defsPrefix + tikzBodyLines.join("\n") },
                });
                continue;
            }

            // Parse paragraph content (any line with content, not just non-backslash)
            if (line && !line.startsWith(`\\end{`) && !line.startsWith(`\\begin{`)) {
                envContent.push({
                    type: "paragraph",
                    content: parseInline(line),
                });
            }

            i++;
        }

        return envContent.length > 0
            ? envContent
            : [{ type: "paragraph", content: [] }];
    }

    // Main parsing loop
    while (i < lines.length) {
        skipEmpty();
        if (i >= lines.length) break;

        const line = lines[i].trim();

        // Skip \title and \author (already parsed from preamble)
        if (line.startsWith("\\title") || line.startsWith("\\author")) {
            i++;
            continue;
        }

        // Skip AMSTeX topmatter block markers and content we've already parsed
        if (line.match(/^\\(topmatter|endtopmatter|endtitle|endauthor|endaddress|endemail|endkeywords|endsubjclass|enddedicatory|endabstract)\b/)) {
            i++;
            continue;
        }

        // Skip amsart and AMSTeX specific metadata commands
        if (line.match(/^\\(address|email|thanks|affiliation|urladdr|curraddr|dedicatory|keywords|subjclass|markboth|thispagestyle|pagestyle|hfill|bigbreak|noindent|medbreak|smallbreak|vspace|hspace|vskip|hskip|par|footnote|abstract)\b/)) {
            i++;
            continue;
        }

        // \maketitle — cosmetic command, skip
        if (line.startsWith("\\maketitle")) {
            i++;
            continue;
        }

        // \tableofcontents — insert a live TOC node
        if (line.startsWith("\\tableofcontents")) {
            content.push({ type: "docToc" });
            i++;
            continue;
        }

        // \newpage, \clearpage, \pagebreak — skip page break commands
        if (line.match(/^\\(newpage|clearpage|pagebreak)/)) {
            i++;
            continue;
        }

        // \date{...}
        if (line.startsWith("\\date")) {
            const date = parseCommand(line, "date");
            if (date) {
                content.push({
                    type: "docDate",
                    content: parseInline(date),
                });
            }
            i++;
            continue;
        }

        // \begin{abstract}
        if (line.startsWith("\\begin{abstract}")) {
            const abstractContent = parseEnvironment("abstract");
            content.push({
                type: "docAbstract",
                content: abstractContent,
            });
            continue;
        }

        // \begin{theorem}, \begin{lemma}, etc.
        // Also handles amsart shorthand names: thm, lem, prop, cor, defn, rem, ex, fac, conj, theo, nota
        // NOTE: parag, parstr, intropar are paragraph wrappers (stripped separately), NOT theorems
        const THME_ENV_MAP: Record<string, string> = {
            theorem: "theorem", thm: "theorem", theo: "theorem", mainthm: "theorem",
            lemma: "lemma", lem: "lemma",
            proposition: "proposition", prop: "proposition",
            corollary: "corollary", cor: "corollary", maincor: "corollary",
            definition: "definition", defn: "definition",
            remark: "remark", rem: "remark",
            example: "example", ex: "example", exam: "example",
            proof: "proof",
            exercise: "exercise",
            conjecture: "conjecture", conj: "conjecture",
            fact: "remark", fac: "remark",
            comment: "remark", com: "remark",
            notation: "remark", nota: "remark",
            claim: "lemma",
        };
        const theoremMatch = line.match(/\\begin\{([a-zA-Z]+)\}(\[([^\]]*)\])?/);
        if (theoremMatch && THME_ENV_MAP[theoremMatch[1]]) {
            const envKey = theoremMatch[1];
            const envType = THME_ENV_MAP[envKey];
            const envName = theoremMatch[3] || "";
            const envContent = parseEnvironment(envKey);
            content.push({
                type: "theoremBlock",
                attrs: { envType, envName },
                content: envContent,
            });
            continue;
        }

        // \begin{equation} or \begin{align} etc.
        // equation (no star) → numbered; equation*, align, align*, gather, multline → display
        // Can appear mid-line after other content
        const mathEnvMatch = line.match(/\\begin\{(equation|align|gather|multline)(\*?)\}/);
        if (mathEnvMatch) {
            const envName = mathEnvMatch[1];
            const starred = mathEnvMatch[2] === "*";
            const envPattern = new RegExp(`\\\\end\\s*\\{${envName}\\*?\\}`);

            // Find where \begin{...} starts
            const beginIdx = line.indexOf(mathEnvMatch[0]);

            // Text before \begin{equation} becomes a paragraph
            const textBefore = line.slice(0, beginIdx).trim();
            if (textBefore) {
                content.push({
                    type: "paragraph",
                    content: parseInline(textBefore),
                });
            }

            // Content after \begin{equation} on same line
            let afterBegin = line.slice(beginIdx + mathEnvMatch[0].length).trim();
            let mathContent = "";

            // Check if \end{...} is on the same line
            if (envPattern.test(afterBegin)) {
                // Single-line equation: \begin{equation} ... \end{equation}
                mathContent = afterBegin.replace(envPattern, "").trim();
                // Check for text after \end{equation} on same line
                const endMatch = afterBegin.match(envPattern);
                if (endMatch) {
                    const endIdx = afterBegin.indexOf(endMatch[0]);
                    mathContent = afterBegin.slice(0, endIdx).trim();
                    const textAfter = afterBegin.slice(endIdx + endMatch[0].length).trim();
                    if (textAfter) {
                        // Reprocess the remainder
                        lines[i] = textAfter;
                        content.push({
                            type: envName === "equation" && !starred ? "numberedEquation" : "displayMath",
                            attrs: { latex: mathContent },
                        });
                        continue;
                    }
                }
            } else {
                // Multi-line equation
                mathContent = afterBegin;
                i++;
                while (i < lines.length && !envPattern.test(lines[i].trim())) {
                    mathContent += " " + lines[i].trim();
                    i++;
                }
                // Check what's on the \end line
                if (i < lines.length) {
                    const endLine = lines[i].trim();
                    const endMatch = endLine.match(envPattern);
                    if (endMatch) {
                        const endIdx = endLine.indexOf(endMatch[0]);
                        mathContent += " " + endLine.slice(0, endIdx).trim();
                        const textAfter = endLine.slice(endIdx + endMatch[0].length).trim();
                        if (textAfter) {
                            lines[i] = textAfter;
                            content.push({
                                type: envName === "equation" && !starred ? "numberedEquation" : "displayMath",
                                attrs: { latex: mathContent.trim() },
                            });
                            continue;
                        }
                    }
                }
            }

            i++; // skip \end line
            content.push({
                type: envName === "equation" && !starred ? "numberedEquation" : "displayMath",
                attrs: { latex: mathContent.trim() },
            });
            continue;
        }

        // $$...$$ display math
        if (line.startsWith("$$")) {
            let mathContent = "";
            // Check if closing $$ is on the same line
            const restOfLine = line.slice(2);
            const closingIdx = restOfLine.indexOf("$$");

            if (closingIdx !== -1) {
                // Single line or inline: $$...$$
                mathContent = restOfLine.slice(0, closingIdx).trim();
                const textAfter = restOfLine.slice(closingIdx + 2).trim();
                content.push({
                    type: "displayMath",
                    attrs: { latex: mathContent },
                });
                // Process text after $$ on same line
                if (textAfter) {
                    lines[i] = textAfter;
                    continue;
                }
            } else {
                // Multi-line: $$ opens, need to find closing $$
                mathContent = restOfLine.trim();
                i++;
                while (i < lines.length) {
                    const nextLine = lines[i].trim();
                    const closeIdx = nextLine.indexOf("$$");
                    if (closeIdx !== -1) {
                        // Found closing $$
                        mathContent += " " + nextLine.slice(0, closeIdx).trim();
                        const textAfter = nextLine.slice(closeIdx + 2).trim();
                        content.push({
                            type: "displayMath",
                            attrs: { latex: mathContent.trim() },
                        });
                        // Process text after $$ on same line
                        if (textAfter) {
                            lines[i] = textAfter;
                        } else {
                            i++;
                        }
                        break;
                    }
                    mathContent += " " + nextLine;
                    i++;
                }
                continue;
            }
            i++;
            continue;
        }

        // \begin{multicols}{2} — two-column section
        if (line.startsWith("\\begin{multicols}")) {
            // Skip optional arg e.g. \begin{multicols}{2}
            const colContent = parseEnvironment("multicols");
            content.push({
                type: "twoColumns",
                content: colContent.length > 0
                    ? colContent
                    : [{ type: "paragraph", content: [] }],
            });
            continue;
        }

        // \twocolumn — rest of document is two-column; wrap remaining content
        if (line.startsWith("\\twocolumn")) {
            const colContent: any[] = [];
            i++;
            // Collect everything until \onecolumn or end of document body
            while (i < lines.length) {
                const l = lines[i].trim();
                if (l.startsWith("\\onecolumn") || l.startsWith("\\end{document}")) break;
                // push back and let the inner parse handle it by calling parseLatex recursively
                colContent.push({ type: "paragraph", content: parseInline(l || "") });
                i++;
            }
            content.push({
                type: "twoColumns",
                content: colContent.length > 0
                    ? colContent
                    : [{ type: "paragraph", content: [] }],
            });
            continue;
        }

        // \onecolumn — just skip the command itself
        if (line.startsWith("\\onecolumn")) {
            i++;
            continue;
        }

        // \begin{itemize}
        if (line.startsWith("\\begin{itemize}")) {
            const items: any[] = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith("\\end{itemize}")) {
                const itemLine = lines[i].trim();
                if (itemLine.startsWith("\\item")) {
                    const text = itemLine.replace(/\\item\s*/, "");
                    items.push({
                        type: "listItem",
                        content: [{ type: "paragraph", content: parseInline(text) }],
                    });
                }
                i++;
            }
            content.push({ type: "bulletList", content: items });
            i++;
            continue;
        }

        // \begin{thebibliography} — skip the entire bibliography block
        if (line.startsWith("\\begin{thebibliography}")) {
            let depth = 1;
            i++;
            while (i < lines.length && depth > 0) {
                const l = lines[i].trim();
                if (l.startsWith("\\end{thebibliography}")) depth--;
                else if (l.startsWith("\\begin{thebibliography}")) depth++;
                i++;
            }
            continue;
        }

        // \begin{enumerate} with optional [label=...] for enumitem styles
        if (line.startsWith("\\begin{enumerate}")) {
            // Detect enumitem label option
            let listStyle: string | null = null;
            const labelMatch = line.match(/\[label\s*=\s*\\textnormal\{\(\\(Roman|roman|Alph|alph)\*\)\}\]/);
            if (labelMatch) {
                const labelMap: Record<string, string> = {
                    "Roman": "upper-roman",
                    "roman": "lower-roman",
                    "Alph": "upper-alpha",
                    "alph": "lower-alpha",
                };
                listStyle = labelMap[labelMatch[1]] ?? null;
            }
            const items: any[] = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith("\\end{enumerate}")) {
                const itemLine = lines[i].trim();
                if (itemLine.startsWith("\\item")) {
                    const text = itemLine.replace(/\\item\s*/, "");
                    items.push({
                        type: "listItem",
                        content: [{ type: "paragraph", content: parseInline(text) }],
                    });
                }
                i++;
            }
            const attrs: any = {};
            if (listStyle) attrs.listStyle = listStyle;
            content.push({ type: "orderedList", attrs, content: items });
            i++;
            continue;
        }

        // \begin{verbatim} or \begin{lstlisting} - code blocks
        if (line.startsWith("\\begin{verbatim}") || line.startsWith("\\begin{lstlisting}")) {
            const envName = line.includes("lstlisting") ? "lstlisting" : "verbatim";
            let codeContent = "";
            i++;
            while (i < lines.length) {
                if (lines[i].trim().startsWith(`\\end{${envName}}`)) break;
                // Handle \end{} appended directly at the end of a code line (no preceding newline)
                const inlineEnd = rawLines[i].indexOf(`\\end{${envName}}`);
                if (inlineEnd !== -1) {
                    const before = rawLines[i].substring(0, inlineEnd);
                    if (before) codeContent += (codeContent ? "\n" : "") + before;
                    break;
                }
                // Preserve original line (including whitespace) for code
                codeContent += (codeContent ? "\n" : "") + rawLines[i];
                i++;
            }
            content.push({
                type: "codeBlock",
                content: codeContent ? [{ type: "text", text: codeContent }] : [],
            });
            i++; // skip \end line
            continue;
        }

        // \begin{figure} — parse into figureBlock or tikzBlock (if it contains tikzpicture)
        if (line.startsWith("\\begin{figure}")) {
            let caption = "";
            let label = "";
            let filename = "";
            let width = "0.8";
            let tikzSource = "";
            i++;
            while (i < lines.length && !lines[i].trim().startsWith("\\end{figure}")) {
                const fline = lines[i].trim();
                // Check for tikzpicture/tikzcd inside figure
                const tikzEnvMatch = fline.match(/^\\begin\{(tikzpicture|tikzcd)\}/);
                if (tikzEnvMatch) {
                    const envName = tikzEnvMatch[1];
                    const bodyLines: string[] = [];
                    bodyLines.push(lines[i]);
                    let depth = 1;
                    i++;
                    while (i < lines.length && depth > 0) {
                        const l = lines[i];
                        const trimmed = l.trim();
                        if (trimmed.startsWith(`\\end{${envName}}`)) depth--;
                        else if (trimmed.match(new RegExp(`^\\\\begin\\{${envName}\\*?\\}`))) depth++;
                        bodyLines.push(l);
                        i++;
                    }
                    const defsPrefix = collectedDefs.length > 0 ? collectedDefs.join("\n") + "\n" : "";
                    tikzSource = defsPrefix + bodyLines.join("\n");
                    continue;
                }
                // \includegraphics[width=0.8\textwidth]{file.png}
                const igMatch = fline.match(/\\includegraphics(?:\[([^\]]*)\])?\{([^}]+)\}/);
                if (igMatch) {
                    const opts = igMatch[1] || "";
                    filename = igMatch[2];
                    const wMatch = opts.match(/width\s*=\s*([0-9.]+)/);
                    if (wMatch) width = wMatch[1];
                }
                const capMatch = fline.match(/\\caption\{([^}]*)\}/);
                if (capMatch) caption = capMatch[1];
                const lblMatch = fline.match(/\\label\{([^}]*)\}/);
                if (lblMatch) label = lblMatch[1];
                i++;
            }
            if (tikzSource) {
                content.push({
                    type: "tikzBlock",
                    attrs: { source: tikzSource, caption, label },
                });
            } else {
                content.push({
                    type: "figureBlock",
                    attrs: { src: "", filename, caption, label, width },
                });
            }
            i++; // skip \end{figure}
            continue;
        }

        // TikZ picture: capture entire environment as a tikzBlock node
        const tikzMatch = line.match(/^\\begin\{(tikzpicture|tikzcd)\}(.*)/);
        if (tikzMatch) {
            const envName = tikzMatch[1];
            const bodyLines: string[] = [];

            // Look back for tikz setup commands immediately preceding \begin{tikzpicture}
            // (e.g. \tdplotsetmaincoords, \pgfmathsetmacro, etc.)
            const setupRe = /^\\(tdplotsetmaincoords|pgfmathsetmacro|tikzset|pgfplotsset|tdplotsetrotatedcoords)\b/;
            while (content.length > 0) {
                const prev = content[content.length - 1];
                if (prev.type === "paragraph" && prev.content?.length === 1 && prev.content[0].type === "text") {
                    const prevText = prev.content[0].text?.trim() ?? "";
                    if (setupRe.test(prevText)) {
                        bodyLines.unshift(prevText);
                        content.pop();
                        continue;
                    }
                }
                break;
            }

            // Include the opening line itself (with options like [>=stealth])
            bodyLines.push(line);
            let depth = 1;
            i++;
            while (i < lines.length && depth > 0) {
                const l = lines[i];
                const trimmed = l.trim();
                if (trimmed.startsWith(`\\end{${envName}}`)) depth--;
                else if (trimmed.match(new RegExp(`^\\\\begin\\{${envName}\\*?\\}`))) depth++;
                bodyLines.push(l);
                i++;
            }
            // Prepend any \def commands from the document so TikZJax has them
            const defsPrefix = collectedDefs.length > 0 ? collectedDefs.join("\n") + "\n" : "";
            content.push({
                type: "tikzBlock",
                attrs: { source: defsPrefix + bodyLines.join("\n") },
            });
            continue;
        }

        // Generic catch-all: skip any \begin{unknown}...\end{unknown} block we don't handle
        // (e.g. \begin{center}, \begin{xy}, etc.)
        const unknownEnvMatch = line.match(/^\\begin\{([^}]+)\}/);
        if (unknownEnvMatch) {
            const envName = unknownEnvMatch[1].replace(/\*$/, "");
            let depth = 1;
            i++;
            while (i < lines.length && depth > 0) {
                const l = lines[i].trim();
                if (l.startsWith(`\\end{${envName}}`)) depth--;
                else if (l.match(new RegExp(`^\\\\begin\\{${envName}\\*?\\}`))) depth++;
                i++;
            }
            continue;
        }

        // AMSTeX-style \head ... \endhead sections
        if (line.startsWith("\\head")) {
            let headContent = "";
            // Check if the header content starts on the same line
            const sameLine = line.replace(/^\\head\s*/, "");
            if (sameLine) {
                headContent = sameLine;
            }
            i++;
            // Collect content until \endhead
            while (i < lines.length) {
                const nextLine = lines[i].trim();
                if (nextLine.startsWith("\\endhead")) {
                    i++;
                    break;
                }
                headContent += (headContent ? " " : "") + nextLine;
                i++;
            }
            if (headContent.trim()) {
                content.push({
                    type: "heading",
                    attrs: { level: 1 },
                    content: parseInline(headContent.trim()),
                });
            }
            continue;
        }

        // AMSTeX-style \subhead ... \endsubhead
        if (line.startsWith("\\subhead")) {
            let headContent = "";
            const sameLine = line.replace(/^\\subhead\s*/, "");
            if (sameLine) headContent = sameLine;
            i++;
            while (i < lines.length) {
                const nextLine = lines[i].trim();
                if (nextLine.startsWith("\\endsubhead")) {
                    i++;
                    break;
                }
                headContent += (headContent ? " " : "") + nextLine;
                i++;
            }
            if (headContent.trim()) {
                content.push({
                    type: "heading",
                    attrs: { level: 2 },
                    content: parseInline(headContent.trim()),
                });
            }
            continue;
        }

        // \section{...} and \section*{...} - can appear anywhere on line
        // Handles: \section{Title}, \section*{Title}, \section {Title}, \section {No...} etc.
        const sectionIdx = line.indexOf("\\section");
        if (sectionIdx !== -1) {
            // Text before \section becomes a paragraph
            const textBefore = line.slice(0, sectionIdx).trim();
            if (textBefore && textBefore !== "\\end{parag}" && textBefore !== "\\end{intropar}") {
                content.push({
                    type: "paragraph",
                    content: parseInline(textBefore),
                });
            }
            const sectionPart = line.slice(sectionIdx);
            // Allow spaces before the brace: \section {Title} or \section*{Title}
            const sectionMatch = sectionPart.match(/\\section\*?\s*(?:\[[^\]]*\])?\s*\{([^}]+)\}(.*)/);
            const title = sectionMatch?.[1] ?? null;
            if (title) {
                content.push({
                    type: "heading",
                    attrs: { level: 1 },
                    content: parseInline(title.trim()),
                });
            }
            // Process any remaining content on the same line
            const remainder = sectionMatch?.[2]?.trim();
            if (remainder) {
                lines[i] = remainder;
                continue;
            }
            i++;
            continue;
        }

        // \subsection{...} and \subsection*{...} - can appear anywhere on line
        const subsectionIdx = line.indexOf("\\subsection");
        if (subsectionIdx !== -1) {
            const textBefore = line.slice(0, subsectionIdx).trim();
            if (textBefore) {
                content.push({
                    type: "paragraph",
                    content: parseInline(textBefore),
                });
            }
            const subsectionPart = line.slice(subsectionIdx);
            const subsectionMatch = subsectionPart.match(/\\subsection\*?\s*(?:\[[^\]]*\])?\s*\{([^}]+)\}(.*)/);
            const title = subsectionMatch?.[1] ?? null;
            if (title) {
                content.push({
                    type: "heading",
                    attrs: { level: 2 },
                    content: parseInline(title.trim()),
                });
            }
            const remainder = subsectionMatch?.[2]?.trim();
            if (remainder) {
                lines[i] = remainder;
                continue;
            }
            i++;
            continue;
        }

        // \subsubsection{...} and \subsubsection*{...} - can appear anywhere on line
        const subsubsectionIdx = line.indexOf("\\subsubsection");
        if (subsubsectionIdx !== -1) {
            const textBefore = line.slice(0, subsubsectionIdx).trim();
            if (textBefore) {
                content.push({
                    type: "paragraph",
                    content: parseInline(textBefore),
                });
            }
            const subsubPart = line.slice(subsubsectionIdx);
            const subsubMatch = subsubPart.match(/\\subsubsection\*?\s*(?:\[[^\]]*\])?\s*\{([^}]+)\}(.*)/);
            const title = subsubMatch?.[1] ?? null;
            if (title) {
                content.push({
                    type: "heading",
                    attrs: { level: 3 },
                    content: parseInline(title.trim()),
                });
            }
            const remainder = subsubMatch?.[2]?.trim();
            if (remainder) {
                lines[i] = remainder;
                continue;
            }
            i++;
            continue;
        }

        // \subparagraph{...} — must be checked before \paragraph
        const subparagraphIdx = line.indexOf("\\subparagraph");
        if (subparagraphIdx !== -1) {
            const textBefore = line.slice(0, subparagraphIdx).trim();
            if (textBefore) {
                content.push({ type: "paragraph", content: parseInline(textBefore) });
            }
            const subparaMatch = line.slice(subparagraphIdx).match(/\\subparagraph\*?\s*(?:\[[^\]]*\])?\s*\{([^}]+)\}(.*)/);
            const title = subparaMatch?.[1] ?? null;
            if (title) {
                content.push({ type: "heading", attrs: { level: 5 }, content: parseInline(title.trim()) });
            }
            const remainder = subparaMatch?.[2]?.trim();
            if (remainder) { lines[i] = remainder; continue; }
            i++;
            continue;
        }

        // \paragraph{...}
        const paragraphIdx = line.indexOf("\\paragraph");
        if (paragraphIdx !== -1) {
            const textBefore = line.slice(0, paragraphIdx).trim();
            if (textBefore) {
                content.push({ type: "paragraph", content: parseInline(textBefore) });
            }
            const paraMatch = line.slice(paragraphIdx).match(/\\paragraph\*?\s*(?:\[[^\]]*\])?\s*\{([^}]+)\}(.*)/);
            const title = paraMatch?.[1] ?? null;
            if (title) {
                content.push({ type: "heading", attrs: { level: 4 }, content: parseInline(title.trim()) });
            }
            const remainder = paraMatch?.[2]?.trim();
            if (remainder) { lines[i] = remainder; continue; }
            i++;
            continue;
        }

        // Strip standalone \par commands (paragraph break in LaTeX)
        // Note: \begin{parag}, \end{parag}, etc. are stripped in preprocessing
        if (line === "\\par" || line === "\\par ") {
            i++;
            continue;
        }
        // Strip \par at start of line
        if (line.startsWith("\\par ")) {
            lines[i] = line.slice(5);
            continue;
        }

        // Display math \[ ... \] - can appear at end of line or start
        if (line.includes("\\[")) {
            // Text before \[ becomes a paragraph
            const bracketIdx = line.indexOf("\\[");
            const textBefore = line.slice(0, bracketIdx).trim();
            if (textBefore) {
                content.push({
                    type: "paragraph",
                    content: parseInline(textBefore),
                });
            }

            let mathContent = "";
            const afterBracket = line.slice(bracketIdx + 2).trim();

            // Check if closing \] is on the same line
            if (afterBracket.includes("\\]")) {
                mathContent = afterBracket.replace("\\]", "").trim();
            } else {
                // Multi-line display math - collect until \]
                mathContent = afterBracket;
                i++;
                while (i < lines.length) {
                    const nextLine = lines[i].trim();
                    if (nextLine.includes("\\]")) {
                        const closingIdx = nextLine.indexOf("\\]");
                        mathContent += " " + nextLine.slice(0, closingIdx).trim();
                        i++;
                        break;
                    }
                    mathContent += " " + nextLine;
                    i++;
                }
            }
            content.push({
                type: "displayMath",
                attrs: { latex: mathContent.trim() },
            });
            if (!line.slice(bracketIdx).includes("\\]")) continue; // Already incremented i in loop
            i++;
            continue;
        }

        // Regular paragraph (including lines that start with unrecognized commands)
        if (line) {
            content.push({
                type: "paragraph",
                content: parseInline(line),
            });
        }

        i++;
    }

    const result = {
        type: "doc",
        content: (() => {
            const body = content.length > 0 ? content : [{ type: "paragraph" }];
            if (!isTwoColumnDoc) return body;
            // Separate any leading metadata nodes (title, author, date, abstract)
            // from the body — they sit above the two-column section
            const metaTypes = new Set(["docTitle", "docAuthor", "docDate", "docAbstract", "docToc"]);
            const metaNodes: any[] = [];
            let firstBodyIdx = 0;
            while (firstBodyIdx < body.length && metaTypes.has(body[firstBodyIdx].type)) {
                metaNodes.push(body[firstBodyIdx]);
                firstBodyIdx++;
            }
            const bodyNodes = body.slice(firstBodyIdx);
            if (bodyNodes.length === 0) return body;
            return [
                ...metaNodes,
                {
                    type: "twoColumns",
                    content: bodyNodes,
                },
            ];
        })(),
    };
    return result;
}
